import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  BankOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  LockOutlined,
  UploadOutlined,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { RadioCard } from '../../components/ui/RadioCard'
import { SegmentedControl } from '../../components/ui/SegmentedControl'
import { cx } from '../../components/ui/cx'
import type {
  AchFormFields,
  CardFormFields,
  FieldErrors,
  FieldRefRegistrar,
  PaymentMethodKind,
  PoGroupDraft,
  SavedPaymentMethod,
} from './checkout-domain'

export type PaymentSelectionMode = 'saved' | 'new'

export interface PaymentStepProps {
  readonly eligibilityStatus: 'loading' | 'loaded' | 'error'
  readonly allowedMethods: readonly PaymentMethodKind[]
  readonly kind: PaymentMethodKind
  readonly onKindChange: (kind: PaymentMethodKind) => void

  readonly savedPaymentMethods: readonly SavedPaymentMethod[]
  readonly mode: PaymentSelectionMode
  readonly selectedSavedId: string | undefined
  readonly onModeChange: (mode: PaymentSelectionMode) => void
  readonly onSelectSaved: (id: string) => void
  /** UID-97 K3 — removes a saved payment method (`DELETE
   * /account/payment-methods/{id}`); the removed card leaves the list. */
  readonly onRemoveSaved: (id: string) => Promise<void>

  readonly card: CardFormFields
  readonly onCardFieldChange: (field: keyof CardFormFields, value: string) => void
  readonly onCardFieldBlur: (field: keyof CardFormFields) => void
  readonly cardErrors: FieldErrors

  readonly ach: AchFormFields
  readonly onAchFieldChange: (field: keyof AchFormFields, value: string) => void
  readonly onAchFieldBlur: (field: keyof AchFormFields) => void
  readonly achErrors: FieldErrors

  readonly poNumber: string
  readonly onPoNumberChange: (value: string) => void
  readonly onPoNumberBlur: () => void
  readonly poErrors: FieldErrors

  /** UID-97 K1 — when split-PO is on, one PO-number input per group
   * (prototype `co.poGroups`, `Fictiv Platform.dc.html` lines 2107-2115)
   * REPLACES the single PO-number field above. `undefined`/empty when
   * split is off. */
  readonly poGroups?: readonly PoGroupDraft[] | undefined
  readonly poGroupNumbers?: Readonly<Record<string, string>> | undefined
  readonly onPoGroupNumberChange?: ((groupKey: string, value: string) => void) | undefined
  /** Gate 5 C1 — every group's PO is REQUIRED when paying by PO invoice
   * (unlike a non-PO-invoice split, where a group PO is optional); keyed by
   * the same `PoGroupDraft.key` as `poGroupNumbers` (`validatePoGroupNumbers`,
   * checkout-domain.ts). */
  readonly poGroupErrors?: Readonly<FieldErrors> | undefined

  readonly registerFieldRef: FieldRefRegistrar
}

const ALL_SEGMENTS: ReadonlyArray<{
  readonly value: PaymentMethodKind
  readonly label: string
  readonly icon: ReactNode
}> = [
  { value: 'card', label: 'Credit card', icon: <CreditCardOutlined /> },
  { value: 'ach', label: 'ACH transfer', icon: <BankOutlined /> },
  { value: 'po_invoice', label: 'Purchase order', icon: <FileTextOutlined /> },
]

/**
 * Format a card expiry as `MM/YY` while typing: keep digits only (max 4) and
 * auto-insert the `/` once the buyer starts the year. Backspacing past the
 * slash drops it (digits ≤ 2 → no slash), so deletion feels natural. Paste of
 * "1226" or "12/26" both normalize to "12/26".
 */
function formatExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4)
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`
}

/**
 * One saved card/ACH row (UID-97 K3, ADR-0044 §2). The Remove control is
 * rendered as a SIBLING of the selection `<label>` — not inside the shared
 * `RadioCard`'s checked-only `children` slot — so it stays visible on EVERY
 * saved method, matching the prototype's per-row Remove (`Fictiv
 * Platform.dc.html` line 2064, unconditional inside the `sc-for`), not just
 * the currently-selected one.
 */
function SavedPaymentMethodRow({
  method,
  checked,
  onSelect,
  onRemove,
}: {
  readonly method: SavedPaymentMethod
  readonly checked: boolean
  readonly onSelect: (id: string) => void
  readonly onRemove: (id: string) => void
}) {
  const brandLabel = method.brand ?? (method.kind === 'ach' ? 'Bank account' : 'Card')
  const radioId = `checkout-saved-payment-${method.id}`
  return (
    <div className={cx('radio-card', checked && 'radio-card-checked')}>
      <label htmlFor={radioId} className="checkout-address-card-select">
        <input
          id={radioId}
          type="radio"
          name="payment-saved-method"
          value={method.id}
          checked={checked}
          onChange={() => onSelect(method.id)}
          className="radio-card-input"
        />
        <span className="radio-card-body">
          <span className="radio-card-heading-row">
            <span className="radio-card-label">
              <span className="checkout-saved-payment-brand">{brandLabel}</span> •••• {method.last4}
            </span>
          </span>
          <span className="radio-card-description">
            {method.holderName}
            {method.expLabel ? ` · exp ${method.expLabel}` : ''}
          </span>
        </span>
      </label>
      <div className="checkout-address-card-actions">
        <Button
          variant="link"
          size="sm"
          aria-label={`Remove ${brandLabel} ending ${method.last4}`}
          onClick={() => onRemove(method.id)}
        >
          Remove
        </Button>
      </div>
    </div>
  )
}

/**
 * §3 of the single-page checkout (Stage-2b) — "How would you like to pay?"
 * Segmented Credit card / ACH transfer / Purchase order tabs
 * (`SegmentedControl`, gated by `GET /checkout/payment-eligibility` exactly
 * as UID-60 gated it — advisory/display-only, ADR-0024/0038; the server
 * independently re-enforces at placement). Card/ACH each list
 * `CheckoutView.savedPaymentMethods` (Stage-2a, TOKENS ONLY) as selectable
 * radio rows plus a "Use a new card"/"Use a new bank account" row that
 * reveals the existing raw-entry fieldset — exactly one of
 * `savedPaymentMethodId` or the new-instrument fields resolves onto the
 * placement request (checkout-domain's PCI boundary is unchanged: this
 * component never receives or emits a full PAN/CVV/routing/account number
 * outside local state, see CheckoutPage's module doc comment).
 */
export function PaymentStep({
  eligibilityStatus,
  allowedMethods,
  kind,
  onKindChange,
  savedPaymentMethods,
  mode,
  selectedSavedId,
  onModeChange,
  onSelectSaved,
  onRemoveSaved,
  card,
  onCardFieldChange,
  onCardFieldBlur,
  cardErrors,
  ach,
  onAchFieldChange,
  onAchFieldBlur,
  achErrors,
  poNumber,
  onPoNumberChange,
  onPoNumberBlur,
  poErrors,
  poGroups,
  poGroupNumbers,
  onPoGroupNumberChange,
  poGroupErrors,
  registerFieldRef,
}: PaymentStepProps) {
  const options = ALL_SEGMENTS.map((segment) => {
    const disabled = !allowedMethods.includes(segment.value)
    if (!disabled) return segment
    const disabledReason =
      eligibilityStatus === 'error'
        ? "We couldn't confirm purchase-order eligibility right now — try again shortly, or use Credit card/ACH."
        : 'available for approved accounts. Contact your account team to learn more.'
    return { ...segment, disabled, disabledReason }
  })

  const savedForKind = savedPaymentMethods.filter((method) => method.kind === kind)
  // Demo-only, mirrors the prototype's local `coCardSave` (default on). There is
  // no "save payment method" field in the checkout contract, so this never
  // leaves the browser — a visual affordance matching the design.
  const [saveCard, setSaveCard] = useState(true)

  return (
    <div className="checkout-payment-step">
      <SegmentedControl
        legend="Payment method"
        name="payment-method"
        options={options}
        value={kind}
        onChange={(value) => onKindChange(value as PaymentMethodKind)}
        loading={eligibilityStatus === 'loading'}
      />

      {eligibilityStatus !== 'loading' && (kind === 'card' || kind === 'ach') && (
        <fieldset className="checkout-payment-fieldset">
          <legend className="sr-only">{kind === 'card' ? 'Card' : 'Bank account'} selection</legend>
          {savedForKind.map((method) => (
            <SavedPaymentMethodRow
              key={method.id}
              method={method}
              checked={mode === 'saved' && selectedSavedId === method.id}
              onSelect={(id) => {
                onModeChange('saved')
                onSelectSaved(id)
              }}
              onRemove={(id) => void onRemoveSaved(id)}
            />
          ))}
          {/* The "Use a new …" pill only appears when there ARE saved methods to
              choose between (design: card has a saved Visa → pill; ACH has none →
              the form shows directly, no pill). */}
          {savedForKind.length > 0 && (
            <RadioCard
              name="payment-saved-method"
              value="new"
              checked={mode === 'new'}
              onChange={() => onModeChange('new')}
              className="checkout-use-new-pill"
              label={kind === 'card' ? 'Use a new card' : 'Use a new bank account'}
            />
          )}
          {/* Design (Fictiv Platform.dc.html:2069) renders the new-instrument
              fields in a SEPARATE neutral-bordered container BELOW the "Use a new
              card" pill — not nested inside the tinted selected card. Shown while
              "new" is chosen (or whenever there are no saved methods to pick). */}
          {(mode === 'new' || savedForKind.length === 0) &&
            (kind === 'card' ? (
              <div className="checkout-new-card-form">
                <Input
                  ref={registerFieldRef('cardNumber')}
                  label="Card number"
                  placeholder="1234 1234 1234 1234"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={card.cardNumber}
                  onChange={(event) => onCardFieldChange('cardNumber', event.target.value)}
                  onBlur={() => onCardFieldBlur('cardNumber')}
                  error={cardErrors.cardNumber}
                />
                <div className="checkout-address-form-row">
                  <Input
                    ref={registerFieldRef('expiry')}
                    label="Expiration"
                    placeholder="MM/YY"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    value={card.expiry}
                    onChange={(event) =>
                      onCardFieldChange('expiry', formatExpiry(event.target.value))
                    }
                    onBlur={() => onCardFieldBlur('expiry')}
                    error={cardErrors.expiry}
                  />
                  <Input
                    ref={registerFieldRef('cvc')}
                    label="CVC"
                    placeholder="123"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    mask
                    value={card.cvc}
                    onChange={(event) => onCardFieldChange('cvc', event.target.value)}
                    onBlur={() => onCardFieldBlur('cvc')}
                    error={cardErrors.cvc}
                  />
                </div>
                <div className="checkout-address-form-row">
                  <Input
                    ref={registerFieldRef('nameOnCard')}
                    label="Name on card"
                    placeholder="Full name"
                    autoComplete="cc-name"
                    value={card.nameOnCard}
                    onChange={(event) => onCardFieldChange('nameOnCard', event.target.value)}
                    onBlur={() => onCardFieldBlur('nameOnCard')}
                    error={cardErrors.nameOnCard}
                  />
                  <Input
                    ref={registerFieldRef('billingZip')}
                    label="Billing ZIP"
                    placeholder="94103"
                    autoComplete="postal-code"
                    value={card.billingZip}
                    onChange={(event) => onCardFieldChange('billingZip', event.target.value)}
                    onBlur={() => onCardFieldBlur('billingZip')}
                    error={cardErrors.billingZip}
                  />
                </div>
                <label className="checkout-save-card-row">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={saveCard}
                    onChange={(event) => setSaveCard(event.target.checked)}
                  />
                  Save this card for future orders
                </label>
              </div>
            ) : (
              <div className="checkout-new-card-form">
                <div className="checkout-address-form-row">
                  <Input
                    ref={registerFieldRef('routingNumber')}
                    label="Routing number"
                    placeholder="9 digits"
                    inputMode="numeric"
                    mask
                    value={ach.routingNumber}
                    onChange={(event) => onAchFieldChange('routingNumber', event.target.value)}
                    onBlur={() => onAchFieldBlur('routingNumber')}
                    error={achErrors.routingNumber}
                  />
                  <Input
                    ref={registerFieldRef('accountNumber')}
                    label="Account number"
                    placeholder="Account number"
                    inputMode="numeric"
                    mask
                    value={ach.accountNumber}
                    onChange={(event) => onAchFieldChange('accountNumber', event.target.value)}
                    onBlur={() => onAchFieldBlur('accountNumber')}
                    error={achErrors.accountNumber}
                  />
                </div>
                <fieldset
                  className="checkout-account-type-fieldset"
                  aria-describedby={achErrors.accountType ? 'ach-account-type-error' : undefined}
                >
                  <legend className="field-label">Account type</legend>
                  <div className="checkout-account-type-options">
                    <label className="checkout-account-type-option">
                      <input
                        ref={registerFieldRef('accountType')}
                        type="radio"
                        name="ach-account-type"
                        value="checking"
                        checked={ach.accountType === 'checking'}
                        onChange={() => onAchFieldChange('accountType', 'checking')}
                      />
                      Checking
                    </label>
                    <label className="checkout-account-type-option">
                      <input
                        type="radio"
                        name="ach-account-type"
                        value="savings"
                        checked={ach.accountType === 'savings'}
                        onChange={() => onAchFieldChange('accountType', 'savings')}
                      />
                      Savings
                    </label>
                  </div>
                  {achErrors.accountType && (
                    <p id="ach-account-type-error" className="input-error-text">
                      {achErrors.accountType}
                    </p>
                  )}
                </fieldset>
                {/* Prototype's reassurance note (Fictiv Platform.dc.html:2099). */}
                <p className="checkout-ach-secure-note">
                  <LockOutlined aria-hidden="true" />
                  Bank details are encrypted and processed securely.
                </p>
              </div>
            ))}
        </fieldset>
      )}

      {eligibilityStatus !== 'loading' && kind === 'po_invoice' && (
        <div className="checkout-new-card-form">
          {poGroups && poGroups.length > 1 ? (
            <>
              <p className="caption-text">
                You chose to place separate POs per group in the cart. Enter a PO number for each.
              </p>
              {poGroups.map((group) => (
                <Input
                  key={group.key}
                  ref={registerFieldRef(group.key)}
                  label={group.label}
                  required
                  placeholder="PO number"
                  value={poGroupNumbers?.[group.key] ?? ''}
                  onChange={(event) => onPoGroupNumberChange?.(group.key, event.target.value)}
                  error={poGroupErrors?.[group.key]}
                />
              ))}
            </>
          ) : (
            <Input
              ref={registerFieldRef('poNumber')}
              label="PO number"
              required
              placeholder="e.g. PO-2026-0142"
              value={poNumber}
              onChange={(event) => onPoNumberChange(event.target.value)}
              onBlur={onPoNumberBlur}
              error={poErrors.poNumber}
            />
          )}
          {/* Signed-PO upload affordance (prototype :2114). Display-only in the
              demo — there is no upload endpoint — so it's a non-interactive dashed
              row, matching the design without pretending to accept a file. */}
          <div className="checkout-po-upload-row" aria-hidden="true">
            <UploadOutlined />
            <span>Upload signed PO document (optional)</span>
          </div>
          <p className="checkout-po-terms-footnote caption-text">
            Net 30 terms &middot; invoiced on shipment. Subject to credit approval.
          </p>
        </div>
      )}
    </div>
  )
}
